import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { kaireraAPI } from '../lib/api';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip
} from 'recharts';
import {
  Brain, Loader2, Lock, TrendingUp, MapPin, DollarSign,
  GraduationCap, AlertTriangle, Target, Briefcase, Users,
  Heart, Shield, Award, ArrowUpRight, Landmark, Play,
  Share2, BarChart3, Sparkles, ChevronRight, Clock,
  Code, ArrowRight, Zap, CheckCircle, Plus, X, Edit3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const ICON_MAP = {
  code: Code, users: Users, brain: Brain, heart: Heart, target: Target,
  shield: Shield, 'trending-up': TrendingUp, share: Share2, award: Award,
  'arrow-up-right': ArrowUpRight, landmark: Landmark, 'alert-triangle': AlertTriangle,
};

const CARD_TYPE_CONFIG = {
  industry_trend: { title: 'Industry Outlook', icon: TrendingUp, color: '#0D9488' },
  role_demand: { title: 'Role Demand', icon: Briefcase, color: '#3B82F6' },
  career_stage: { title: 'Career Stage', icon: Clock, color: '#1B3461' },
  company_intel: { title: 'Company Intelligence', icon: Briefcase, color: '#16A34A' },
  salary_benchmark: { title: 'Salary Benchmark', icon: DollarSign, color: '#0D9488' },
  panel_assembly: { title: 'Advisory Panel', icon: Users, color: '#1B3461' },
  feasibility: { title: 'Feasibility', icon: Target, color: '#16A34A' },
  strategic_path: { title: 'Strategic Path', icon: ArrowUpRight, color: '#0D9488' },
  salary_intel: { title: 'Salary Intelligence', icon: DollarSign, color: '#16A34A' },
  credential_gap: { title: 'Credential Gap', icon: GraduationCap, color: '#3B82F6' },
  challenge_diagnostic: { title: 'Challenge Diagnostic', icon: AlertTriangle, color: '#F5841F' },
  risk_profile: { title: 'Risk Profile', icon: Shield, color: '#F5841F' },
  urgency: { title: 'Timeline Urgency', icon: Clock, color: '#DC2626' },
  persona_badge: { title: 'Career Persona', icon: Sparkles, color: '#1B3461' },
  pathway_confirmation: { title: 'Career Goal', icon: Target, color: '#0D9488' },
};

const PATHWAY_LABELS = {
  start_career: 'Start My Career',
  change_career: 'Change Career Path',
  grow_role: 'Grow in Current Role',
  find_role: 'Find a New Role',
  go_abroad: 'Work Internationally',
};

const DIM_DESCRIPTIONS = {
  technical_competency: "Measures your core technical skills, domain expertise, and ability to deliver complex work in your field.",
  leadership_readiness: "Evaluates your capacity to lead teams, make strategic decisions, and inspire others toward shared goals.",
  strategic_thinking: "Assesses your ability to see the big picture, connect disparate ideas, and plan for long-term outcomes.",
  emotional_intelligence: "Measures your self-awareness, empathy, social skills, and ability to manage emotions in professional settings.",
  behavioral_alignment: "Evaluates how well your natural behaviors align with the demands of your target role and industry norms.",
  psychological_fitness: "Assesses your mental resilience, stress tolerance, and ability to maintain performance under pressure.",
  market_position: "Reflects your current standing in the job market relative to peers, skill demand, and industry positioning.",
  network_visibility: "Measures the strength and reach of your professional network, online presence, and industry visibility.",
  credential_strength: "Evaluates the market value of your degrees, certifications, and formal qualifications.",
  career_trajectory: "Tracks your career momentum, progression speed, role complexity growth, and upward trend.",
  political_social_capital: "Assesses your ability to navigate organizational politics, build alliances, and influence without authority.",
  risk_profile: "Measures your appetite for career risk, adaptability to change, and comfort with uncertainty.",
};

const getDimTips = (dimId, score) => {
  const s = score || 0;
  const tips = {
    technical_competency: s < 6
      ? ["Dedicate 30 minutes daily to deliberate practice in your weakest area", "Earn one industry-recognized certification in the next 90 days", "Build a public portfolio project that showcases applied skills"]
      : s < 8
      ? ["Specialize deeper in one niche to become the go-to expert", "Mentor a junior colleague to solidify your own understanding", "Write technical articles to build thought leadership"]
      : ["Transition from individual contributor to technical leadership", "Architect systems at the platform level", "Speak at conferences or publish in your field"],
    leadership_readiness: s < 6
      ? ["Volunteer to lead a small cross-functional initiative", "Practice giving direct, constructive feedback weekly", "Study one leadership framework (situational leadership, servant leadership)"]
      : s < 8
      ? ["Take on a P&L or budget responsibility, even informally", "Build a personal board of advisors from your network", "Lead through influence, not just authority"]
      : ["Seek board or advisory roles outside your company", "Develop succession plans for your current responsibilities", "Coach other emerging leaders"],
    strategic_thinking: s < 6
      ? ["Read your industry's top analyst reports monthly", "Practice mapping cause-and-effect chains for business decisions", "Ask 'why' three levels deep before proposing solutions"]
      : ["Connect with strategy professionals in your network", "Propose a strategic initiative to your leadership", "Study adjacent industries for transferable insights"],
    emotional_intelligence: s < 6
      ? ["Practice active listening: paraphrase before responding", "Keep a brief daily reflection journal on workplace interactions", "Ask for feedback on your communication style from trusted peers"]
      : ["Mediate a conflict between team members", "Develop your coaching skills with structured conversations", "Build psychological safety in your team through vulnerability"],
    behavioral_alignment: s < 6
      ? ["Identify the top 3 behaviors valued in your target role", "Get a 360-degree feedback assessment from your team", "Shadow a high performer in your target role for a week"]
      : ["Align your personal brand with your aspirational role", "Proactively demonstrate target-role behaviors in current work", "Seek stretch assignments that exercise desired behaviors"],
    psychological_fitness: s < 6
      ? ["Develop a stress management routine (meditation, exercise, journaling)", "Set clear work boundaries and honor them consistently", "Build a support network you can call on during tough periods"]
      : ["Take on high-pressure assignments to build resilience", "Develop a personal 'crisis playbook' for career setbacks", "Mentor others through difficult career transitions"],
    market_position: s < 6
      ? ["Update your LinkedIn and resume to reflect current market language", "Research salary benchmarks for your role and region", "Identify 3 companies where your skills are in high demand"]
      : ["Position yourself as a thought leader through content creation", "Build relationships with recruiters in your target market", "Create a personal brand that differentiates you from peers"],
    network_visibility: s < 6
      ? ["Connect with 5 new professionals in your field each week", "Attend one industry event or webinar monthly", "Engage meaningfully on LinkedIn: comment, share, post"]
      : ["Seek speaking opportunities at industry events", "Build a referral network with mutual value exchange", "Create content that positions you as an industry voice"],
    credential_strength: s < 6
      ? ["Identify the most valued certification in your target role", "Audit your education gaps against job descriptions you want", "Build a portfolio that demonstrates practical competency"]
      : ["Pursue advanced credentials that unlock senior roles", "Combine credentials with demonstrated impact for maximum value", "Mentor credential seekers to reinforce your expert status"],
    career_trajectory: s < 6
      ? ["Map your ideal career path for the next 5 years", "Identify the 2-3 skills that will accelerate your next promotion", "Set quarterly career milestones and review progress monthly"]
      : ["Seek stretch roles that accelerate your growth curve", "Build a track record of increasing scope and impact", "Create visibility for your achievements with decision-makers"],
    political_social_capital: s < 6
      ? ["Map the key stakeholders and decision-makers in your organization", "Build genuine relationships with people outside your team", "Learn to read organizational dynamics without taking sides"]
      : ["Become the connector between siloed teams or departments", "Develop sponsors (not just mentors) at senior levels", "Navigate organizational change as a trusted advisor"],
    risk_profile: s < 6
      ? ["Start small: take one calculated career risk this quarter", "Build a financial buffer to enable bolder career moves", "Study the risk profiles of successful people in your field"]
      : ["Channel your risk appetite into high-reward career bets", "Build a diversified skill portfolio to reduce downside risk", "Mentor others on calculated risk-taking in their careers"],
  };
  return tips[dimId] || ["Complete more assessments to unlock personalized recommendations"];
};

const formatCardAnswer = (type, answer) => {
  if (type === 'pathway_confirmation' && typeof answer === 'string') {
    return PATHWAY_LABELS[answer] || answer.replace(/_/g, ' ');
  }
  if (type === 'career_stage' && typeof answer === 'object' && answer !== null) {
    const t = answer.total_years; const c = answer.current_role_years;
    if (t != null && c != null) return `${t} years total experience, ${c} in current role`;
  }
  if ((type === 'salary_benchmark' || type === 'panel_assembly') && typeof answer === 'object' && answer !== null) {
    const parts = [answer.city, answer.country].filter(Boolean);
    const loc = parts.join(', ') || 'Location set';
    return answer.open_to_relocation ? `${loc} (Open to relocation)` : loc;
  }
  if (type === 'salary_intel' && typeof answer === 'object' && answer !== null) {
    const fmt = (v) => v >= 1000 ? `${Math.round(v / 1000)}K` : v;
    const c = answer.currency || ''; const cur = answer.current_salary; const tgt = answer.target_salary;
    if (cur && tgt) return `${c} ${fmt(cur)} current → ${fmt(tgt)} target`.trim();
  }
  if (type === 'credential_gap' && typeof answer === 'object' && answer !== null) {
    return [answer.education_level, answer.certifications].filter(Boolean).join(', ') || 'Credentials assessed';
  }
  if (type === 'risk_profile' && (typeof answer === 'number' || !isNaN(Number(answer)))) {
    return `Risk tolerance: ${answer}/10`;
  }
  if (type === 'urgency' && Array.isArray(answer)) {
    return answer.join(', ');
  }
  if (Array.isArray(answer)) return answer.join(', ');
  if (typeof answer === 'object' && answer !== null) {
    const vals = Object.entries(answer)
      .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
    return vals.join(', ') || String(answer);
  }
  return String(answer).slice(0, 100);
};

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.4 },
});

export default function KaireraDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [panel, setPanel] = useState(null);
  const [batteries, setBatteries] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [goalName, setGoalName] = useState('');
  const [showAllAdvisors, setShowAllAdvisors] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handleDeleteSession = async () => {
    if (!deleteTarget || deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await kaireraAPI.deleteSession(deleteTarget.session_id);
      setSessions(prev => prev.filter(s => s.session_id !== deleteTarget.session_id));
      if (activeSessionId === deleteTarget.session_id) {
        const remaining = sessions.filter(s => s.session_id !== deleteTarget.session_id);
        setActiveSessionId(remaining.length > 0 ? remaining[0].session_id : null);
      }
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } catch (err) {
      console.error('Failed to delete session:', err);
    } finally { setDeleting(false); }
  };

  // Load all sessions on mount
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const res = await kaireraAPI.getSessions();
        const allSessions = res.data.sessions || [];
        setSessions(allSessions);
        // Default to the most progressed session (highest completion_pct)
        if (allSessions.length > 0 && !activeSessionId) {
          const sortedByProgress = [...allSessions].sort((a, b) => (b.completion_pct || 0) - (a.completion_pct || 0));
          setActiveSessionId(sortedByProgress[0].session_id);
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      }
    };
    loadSessions();
  }, []);

  // Load dashboard data for the active session
  useEffect(() => {
    if (!activeSessionId && sessions.length === 0) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [dashRes, panelRes] = await Promise.all([
          kaireraAPI.getDashboard(activeSessionId),
          kaireraAPI.getPanel(activeSessionId),
        ]);
        setData(dashRes.data);
        setPanel(panelRes.data);
        // Load batteries if discovery is complete
        if (dashRes.data?.discovery_complete) {
          try {
            const battRes = await kaireraAPI.getBatteries();
            setBatteries(battRes.data);
          } catch (e) { /* batteries not available yet */ }
        } else {
          setBatteries(null);
        }
      } catch (err) {
        console.error('Dashboard load failed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeSessionId]);

  const handleNewGoal = async () => {
    if (!goalName.trim()) return;
    if (sessions.length >= 3) {
      alert('You can have up to 3 active goals. Archive an existing goal to create a new one.');
      return;
    }
    try {
      const res = await kaireraAPI.createSession('full', true, goalName.trim());
      const newSession = res.data.session;
      const sessRes = await kaireraAPI.getSessions();
      setSessions(sessRes.data.sessions || []);
      setActiveSessionId(newSession.session_id);
      setShowGoalDialog(false);
      setGoalName('');
      navigate('/career-assessment');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create goal');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0D9488' }} />
      </div>
    );
  }

  if (!data?.has_session) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20" data-testid="no-session">
        <Brain className="w-16 h-16 mx-auto mb-6 opacity-30" style={{ color: '#0D9488' }} />
        <h1 className="text-2xl font-extrabold mb-3" style={{ color: 'var(--t-text-heading)' }}>
          Career Intelligence Dashboard
        </h1>
        <p className="text-sm mb-8 max-w-md mx-auto" style={{ color: 'var(--t-text-muted)' }}>
          Start your career assessment to unlock personalized intelligence cards, grades, and an advisory panel tailored to your profile.
        </p>
        <Button onClick={() => navigate('/career-assessment')} data-testid="start-assessment-btn"
          className="h-12 px-8 font-semibold rounded-lg"
          style={{ background: '#0D9488', color: '#fff' }}>
          <Play className="w-5 h-5 mr-2" /> Start Assessment
        </Button>
      </div>
    );
  }

  // Paywall for free users who completed discovery
  if (data?.paywall) {
    const teaser = data.teaser_grades || [];
    const visibleGrades = teaser.filter(g => !g.locked);
    const lockedGrades = teaser.filter(g => g.locked);

    // Build radar data for teaser chart
    const radarTeaser = teaser.map(g => ({
      dimension: g.name?.split(' ')[0] || '?',
      score: g.locked ? 0 : (g.score || 0),
      target: 8,
      fullMark: 10,
    }));

    const unlockFeatures = [
      { icon: Brain, text: 'Full 13-dimension career profile', detail: 'Deep analysis across all career facets' },
      { icon: Target, text: 'Personalized action plans', detail: 'Step-by-step improvement roadmap' },
      { icon: BarChart3, text: 'Peer comparison radar', detail: 'See how you stack up in your industry' },
      { icon: Sparkles, text: 'AI career coaching', detail: 'Intelligent recommendations and insights' },
      { icon: Shield, text: 'Gap analysis & future skills', detail: 'Know exactly where to invest your time' },
      { icon: TrendingUp, text: 'Career trajectory simulator', detail: 'Model your future career path' },
    ];

    return (
      <div className="max-w-3xl mx-auto py-6" data-testid="results-paywall">
        {/* Hero Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}
            className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center relative"
            style={{ background: 'linear-gradient(135deg, #0D9488, #3B82F6)' }}>
            <Award className="w-8 h-8 text-white" />
            <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 2 }}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
              style={{ background: '#F5841F' }}>
              <span className="text-[8px] font-black text-white">!</span>
            </motion.div>
          </motion.div>
          <h1 className="text-2xl font-extrabold mb-2" style={{ color: 'var(--t-text-heading)' }}>
            Your Career Intelligence Is Ready
          </h1>
          <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
            You completed all {data.total_questions} discovery questions. Your full {data.total_dimensions || 13}-dimension profile awaits.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {/* Teaser Radar Chart */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
            className="nx-card p-5 relative overflow-hidden" data-testid="teaser-radar">
            <p className="text-[10px] font-bold tracking-[0.15em] uppercase mb-3" style={{ color: '#0D9488' }}>
              Your Career Radar (Preview)
            </p>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarTeaser} cx="50%" cy="50%">
                  <PolarGrid stroke="var(--t-border)" strokeDasharray="3 3" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 8, fill: 'var(--t-text-muted)' }} />
                  <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                  <Radar name="Target" dataKey="target" stroke="#3B82F630" fill="#3B82F608" strokeWidth={1} strokeDasharray="4 4" />
                  <Radar name="You" dataKey="score" stroke="#0D9488" fill="#0D948820" strokeWidth={2} dot={{ r: 3, fill: '#0D9488' }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {/* Blur overlay on bottom half */}
            <div className="absolute bottom-0 left-0 right-0 h-24 flex items-end justify-center pb-3"
              style={{ background: 'linear-gradient(transparent, var(--t-bg-card-solid))', backdropFilter: 'blur(2px)' }}>
              <div className="flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" style={{ color: '#0D9488' }} />
                <span className="text-[10px] font-bold" style={{ color: '#0D9488' }}>
                  {lockedGrades.length} dimensions hidden
                </span>
              </div>
            </div>
          </motion.div>

          {/* Visible Grades + Locked Tease */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
            className="nx-card p-5" data-testid="teaser-preview">
            <p className="text-[10px] font-bold tracking-[0.15em] uppercase mb-3" style={{ color: '#0D9488' }}>
              Unlocked Dimensions
            </p>
            <div className="space-y-2.5 mb-4">
              {visibleGrades.map((g, i) => (
                <motion.div key={g.dimension} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ background: `${g.color}08`, border: `1px solid ${g.color}20` }}
                  data-testid={`teaser-grade-${g.dimension}`}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${g.color}15` }}>
                    <span className="text-lg font-black" style={{ color: g.color }}>{g.grade}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{g.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="h-1.5 flex-1 rounded-full overflow-hidden" style={{ background: 'var(--t-border-subtle)' }}>
                        <motion.div className="h-full rounded-full" style={{ background: g.color }}
                          initial={{ width: 0 }} animate={{ width: `${(g.score / 10) * 100}%` }}
                          transition={{ delay: 0.8 + i * 0.1, duration: 0.6 }} />
                      </div>
                      <span className="text-xs font-black tabular-nums" style={{ color: g.color }}>{g.score}/10</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Locked grid */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-2">
                {lockedGrades.slice(0, 4).map((g, i) => (
                  <motion.div key={g.dimension} initial={{ opacity: 0 }} animate={{ opacity: 0.4 }}
                    transition={{ delay: 0.8 + i * 0.05 }}
                    className="p-2.5 rounded-lg text-center"
                    style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}>
                    <Lock className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: 'var(--t-text-muted)' }} />
                    <p className="text-[9px] font-semibold truncate" style={{ color: 'var(--t-text-muted)' }}>{g.name}</p>
                  </motion.div>
                ))}
              </div>
              <div className="absolute inset-0 rounded-xl"
                style={{ backdropFilter: 'blur(3px)', background: 'linear-gradient(135deg, rgba(var(--t-bg-page-rgb, 15,23,42), 0.3), rgba(var(--t-bg-page-rgb, 15,23,42), 0.5))' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 2 }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                  style={{ background: 'rgba(13,148,136,0.15)', border: '1px solid rgba(13,148,136,0.3)' }}>
                  <Lock className="w-3 h-3" style={{ color: '#0D9488' }} />
                  <span className="text-[10px] font-bold" style={{ color: '#0D9488' }}>
                    +{lockedGrades.length} locked
                  </span>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* What You'll Unlock */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="nx-card p-5 mb-5" data-testid="unlock-features">
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase mb-4" style={{ color: 'var(--t-text-muted)' }}>
            What You'll Unlock with Pro
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {unlockFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + i * 0.08 }}
                  className="p-3 rounded-xl group"
                  style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}>
                  <Icon className="w-4 h-4 mb-2" style={{ color: '#0D9488' }} />
                  <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--t-text-heading)' }}>{f.text}</p>
                  <p className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>{f.detail}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Free vs Pro Comparison */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
          className="nx-card overflow-hidden mb-5" data-testid="plan-comparison">
          <div className="grid grid-cols-3 text-center">
            <div className="p-3 border-b" style={{ borderColor: 'var(--t-border)' }}>
              <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--t-text-muted)' }}>Feature</span>
            </div>
            <div className="p-3 border-b" style={{ borderColor: 'var(--t-border)', background: 'var(--t-bg-section)' }}>
              <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--t-text-muted)' }}>Free</span>
            </div>
            <div className="p-3 border-b" style={{ borderColor: 'var(--t-border)', background: 'rgba(13,148,136,0.06)' }}>
              <span className="text-[10px] font-bold uppercase" style={{ color: '#0D9488' }}>Pro</span>
            </div>
            {[
              ['Dimensions', '2 of 13', 'All 13'],
              ['Career Grades', 'Preview only', 'Full grades + tips'],
              ['AI Coaching', 'None', 'Unlimited'],
              ['Gap Analysis', 'None', 'Full report'],
              ['Peer Comparison', 'None', 'Industry benchmark'],
            ].map(([feat, free, pro], i) => (
              <React.Fragment key={i}>
                <div className="p-2.5 border-b flex items-center justify-start pl-4" style={{ borderColor: 'var(--t-border)' }}>
                  <span className="text-[10px] font-semibold" style={{ color: 'var(--t-text-heading)' }}>{feat}</span>
                </div>
                <div className="p-2.5 border-b flex items-center justify-center" style={{ borderColor: 'var(--t-border)', background: 'var(--t-bg-section)' }}>
                  <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{free}</span>
                </div>
                <div className="p-2.5 border-b flex items-center justify-center gap-1" style={{ borderColor: 'var(--t-border)', background: 'rgba(13,148,136,0.06)' }}>
                  <CheckCircle className="w-3 h-3" style={{ color: '#0D9488' }} />
                  <span className="text-[10px] font-bold" style={{ color: '#0D9488' }}>{pro}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
          className="nx-card p-6 text-center relative overflow-hidden" style={{ borderColor: 'rgba(13,148,136,0.3)' }} data-testid="paywall-cta">
          {/* Glow effect */}
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-60 h-60 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #0D9488, transparent)' }} />
          <div className="relative">
            <p className="text-sm mb-1 font-semibold" style={{ color: 'var(--t-text-heading)' }}>
              Unlock your full career potential
            </p>
            <p className="text-xs mb-5" style={{ color: 'var(--t-text-muted)' }}>
              Join 10,000+ professionals who leveled up with Career Intelligence.
            </p>
            <Button onClick={() => navigate('/subscription')} data-testid="paywall-upgrade-btn"
              className="h-12 px-10 font-bold rounded-xl w-full sm:w-auto text-sm"
              style={{ background: 'linear-gradient(135deg, #0D9488, #0B7A70)', color: '#fff', boxShadow: '0 4px 20px rgba(13,148,136,0.3)' }}>
              Unlock All {data.total_dimensions || 13} Dimensions <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-[10px] mt-3" style={{ color: 'var(--t-text-muted)' }}>
              Starting at $19/month &middot; Cancel anytime &middot; 7-day free trial
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  const { unlocked_cards, grades, completion_pct, answered_count, total_questions, current_phase, phase_label, discovery_complete } = data;

  const activeSession = sessions.find(s => s.session_id === activeSessionId);

  // Build radar data from grades
  const radarData = grades.filter(g => g.state !== 'locked').map(g => ({
    dimension: g.name.replace(/ /g, '\n'),
    short: g.name.split(' ')[0],
    score: g.score || 0,
    target: 8,
    fullMark: 10,
  }));

  // Overall score (weighted average of graded dimensions)
  const gradedDims = grades.filter(g => g.score !== null);
  const overallScore = gradedDims.length > 0
    ? gradedDims.reduce((sum, g) => sum + (g.score * g.weight), 0) / gradedDims.reduce((sum, g) => sum + g.weight, 0)
    : 0;
  const overallGrade = overallScore >= 9.5 ? 'A+' : overallScore >= 9 ? 'A' : overallScore >= 8.5 ? 'A-' :
    overallScore >= 8 ? 'B+' : overallScore >= 7.5 ? 'B' : overallScore >= 7 ? 'B-' :
    overallScore >= 6.5 ? 'C+' : overallScore >= 6 ? 'C' : overallScore >= 5.5 ? 'C-' :
    overallScore >= 5 ? 'D+' : overallScore >= 4 ? 'D' : 'F';
  const gradeColor = (g) => {
    if (!g) return '#718096';
    if (g.startsWith('A')) return '#16A34A';
    if (g.startsWith('B')) return '#84CC16';
    if (g.startsWith('C')) return '#EAB308';
    if (g.startsWith('D')) return '#F97316';
    return '#DC2626';
  };

  return (
    <div data-testid="kairera-dashboard">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>
              Career Intelligence
            </p>
            <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
              Intelligence Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {sessions.length < 3 && (
              <Button onClick={() => setShowGoalDialog(true)} data-testid="new-goal-btn"
                variant="outline" className="h-9 px-4 font-semibold rounded-lg text-xs gap-1.5"
                style={{ borderColor: 'var(--t-border)', color: 'var(--t-text)' }}>
                <Plus className="w-3.5 h-3.5" /> New Goal
              </Button>
            )}
            <Button onClick={() => navigate(discovery_complete ? '/career-batteries' : '/career-assessment')} data-testid="continue-assessment-btn"
              className="h-10 px-5 font-semibold rounded-lg text-sm"
              style={{ background: '#0D9488', color: '#fff' }}>
              {discovery_complete ? 'Assessment Batteries' : 'Continue Discovery'}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>

        {/* Session Switcher Tabs */}
        {sessions.length > 1 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1" data-testid="session-tabs">
            {sessions.map((s) => {
              const isActive = s.session_id === activeSessionId;
              const isComplete = s.discovery_complete;
              return (
                <div key={s.session_id} className="group flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setActiveSessionId(s.session_id)}
                    data-testid={`session-tab-${s.session_id}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
                    style={{
                      background: isActive ? 'rgba(13,148,136,0.1)' : 'var(--t-bg-section)',
                      border: isActive ? '1.5px solid #0D9488' : '1px solid var(--t-border)',
                      color: isActive ? '#0D9488' : 'var(--t-text-muted)',
                    }}>
                    {isComplete && <CheckCircle className="w-3.5 h-3.5" />}
                    {!isComplete && <Target className="w-3.5 h-3.5" />}
                    <span>{s.goal_name || s.pathway_label || 'Goal ' + (sessions.indexOf(s) + 1)}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                      style={{
                        background: isComplete ? 'rgba(22,163,74,0.1)' : 'rgba(13,148,136,0.08)',
                        color: isComplete ? '#16A34A' : 'var(--t-text-muted)',
                      }}>
                      {s.completion_pct}%
                    </span>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); setDeleteConfirmText(''); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50"
                    data-testid={`delete-session-${s.session_id}`}
                    title="Delete this goal">
                    <X className="w-3 h-3" style={{ color: '#DC2626' }} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Progress */}
        <div className="mt-4 rounded-xl p-4" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold" style={{ color: 'var(--t-text-heading)' }}>
              Discovery Progress
            </span>
            <span className="text-xs font-bold" style={{ color: '#0D9488' }}>
              {answered_count}/{total_questions} questions ({completion_pct}%)
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${completion_pct}%`, background: 'linear-gradient(90deg, #0D9488, #14B8A6)' }} />
          </div>
          <p className="text-[11px] mt-1.5" style={{ color: 'var(--t-text-muted)' }}>
            Phase {current_phase}: {phase_label}
          </p>
        </div>

        {/* Battery Progress (shown when discovery is complete) */}
        {discovery_complete && batteries && (
          <div className="mt-3 rounded-xl p-4" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold" style={{ color: 'var(--t-text-heading)' }}>
                Assessment Batteries
              </span>
              <span className="text-xs font-bold" style={{ color: '#0D9488' }}>
                {batteries.completed_count}/{batteries.total_count} complete
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.round((batteries.completed_count / batteries.total_count) * 100)}%`, background: 'linear-gradient(90deg, #F5841F, #FB923C)' }} />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {batteries.batteries?.map(b => (
                <span key={b.id} className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    background: b.is_complete ? 'rgba(22,163,74,0.1)' : 'var(--t-bg-card)',
                    color: b.is_complete ? '#16A34A' : 'var(--t-text-muted)',
                    border: `1px solid ${b.is_complete ? 'rgba(22,163,74,0.2)' : 'var(--t-border)'}`,
                  }}>
                  {b.is_complete && <span className="mr-0.5">&#10003;</span>}
                  {b.name.split(' ')[0]}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions (when discovery complete) */}
        {discovery_complete && (
          <div className="mt-3 flex gap-2">
            <Button onClick={() => navigate('/career-report')} variant="outline"
              className="flex-1 h-9 text-xs font-semibold rounded-lg"
              style={{ borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
              data-testid="go-to-report-btn">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Intelligence Report
            </Button>
            <Button onClick={() => navigate('/career-tools')} variant="outline"
              className="flex-1 h-9 text-xs font-semibold rounded-lg"
              style={{ borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
              data-testid="go-to-tools-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Tools
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Score & Radar */}
        <div className="lg:col-span-1 space-y-5">
          {/* Overall Score */}
          {gradedDims.length > 0 && (
            <motion.div {...fadeIn(0)} className="nx-card p-6 text-center" data-testid="overall-score-card">
              <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--t-text-muted)' }}>
                Career Readiness
              </p>
              <div className="w-28 h-28 mx-auto rounded-full flex items-center justify-center mb-3"
                style={{ background: `${gradeColor(overallGrade)}12`, border: `3px solid ${gradeColor(overallGrade)}` }}>
                <div>
                  <span className="text-3xl font-black" style={{ color: gradeColor(overallGrade) }}>
                    ~{overallGrade}
                  </span>
                  <p className="text-[9px] font-semibold" style={{ color: 'var(--t-text-muted)' }}>Preliminary</p>
                </div>
              </div>
              <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                Based on {gradedDims.length} of {grades.length} dimensions
              </p>
            </motion.div>
          )}

          {/* Radar Chart */}
          {radarData.length >= 3 && (
            <motion.div {...fadeIn(0.1)} className="nx-card p-5" data-testid="radar-chart">
              <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--t-text-muted)' }}>
                Dimension Radar
              </p>
              <ResponsiveContainer width="100%" height={240}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="var(--t-border)" />
                  <PolarAngleAxis dataKey="short" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]} tick={false} axisLine={false} />
                  <Radar name="Target" dataKey="target" stroke="#1B3461" fill="#1B3461" fillOpacity={0.05} strokeDasharray="4 4" />
                  <Radar name="Your Score" dataKey="score" stroke="#0D9488" fill="#0D9488" fillOpacity={0.2} strokeWidth={2} />
                  <Tooltip contentStyle={{ background: 'var(--t-bg-card)', border: '1px solid var(--t-border)', borderRadius: 8, fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Advisory Panel Preview */}
          {panel?.panel && (
            <motion.div {...fadeIn(0.2)} className="nx-card p-5" data-testid="panel-preview">
              <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--t-text-muted)' }}>
                Your Advisory Panel
              </p>
              <div className="space-y-2">
                {(showAllAdvisors ? panel.panel : panel.panel.slice(0, 5)).map((p, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-2 rounded-lg"
                    style={{ background: 'var(--t-bg-section)' }}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold"
                      style={{ background: 'rgba(13,148,136,0.1)', color: '#0D9488' }}>
                      {p.slot_number || p.slot}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold truncate" style={{ color: 'var(--t-text-heading)' }}>
                        {p.expert_archetype || p.archetype}
                      </p>
                      <p className="text-[9px] truncate" style={{ color: 'var(--t-text-muted)' }}>
                        {p.specialty}
                      </p>
                    </div>
                  </div>
                ))}
                {panel.panel.length > 5 && (
                  <button onClick={() => setShowAllAdvisors(!showAllAdvisors)}
                    className="w-full text-[10px] text-center pt-2 pb-1 font-semibold cursor-pointer transition-colors rounded-md"
                    style={{ color: '#0D9488' }}
                    data-testid="toggle-advisors-btn">
                    {showAllAdvisors ? 'Show less' : `+${panel.panel.length - 5} more advisors`}
                    <ChevronRight className={`w-3 h-3 inline ml-0.5 transition-transform ${showAllAdvisors ? 'rotate-90' : ''}`} />
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Right Column - Intelligence Cards + Grade Grid */}
        <div className="lg:col-span-2 space-y-5">
          {/* Intelligence Cards */}
          {unlocked_cards.length > 0 && (
            <motion.div {...fadeIn(0)} data-testid="intel-cards">
              <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--t-text-muted)' }}>
                Unlocked Intelligence ({unlocked_cards.length})
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {unlocked_cards.map((card, i) => {
                  const config = CARD_TYPE_CONFIG[card.type] || { title: card.type, icon: Brain, color: '#718096' };
                  const CardIcon = config.icon;
                  return (
                    <motion.div key={`${card.type}-${i}`} {...fadeIn(i * 0.05)}
                      className="nx-card p-4 flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${config.color}12` }}>
                        <CardIcon className="w-4 h-4" style={{ color: config.color }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--t-text-heading)' }}>
                          {config.title}
                        </p>
                        <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: 'var(--t-text-muted)' }}>
                          {formatCardAnswer(card.type, card.answer)}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* 12-Dimension Grade Grid */}
          <div data-testid="grade-grid">
            <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--t-text-muted)' }}>
              Career Dimension Grades
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {grades.map((g, i) => {
                const DimIcon = ICON_MAP[g.icon] || Brain;
                const isLocked = g.state === 'locked';
                const isPrelim = g.state === 'preliminary';

                return (
                  <motion.div key={g.dimension} {...fadeIn(i * 0.03)}
                    className={`nx-card p-4 relative overflow-hidden ${!isLocked ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                    style={{
                      opacity: isLocked ? 0.5 : 1,
                      borderColor: isPrelim ? `${g.color}40` : 'var(--t-border)',
                      borderStyle: isPrelim ? 'dashed' : 'solid',
                    }}
                    onClick={() => !isLocked && setSelectedDimension(g)}
                    data-testid={`grade-${g.dimension}`}>
                    {isLocked && (
                      <div className="absolute inset-0 flex items-center justify-center z-10"
                        style={{ background: 'var(--t-bg-card)', opacity: 0.8 }}>
                        <Lock className="w-5 h-5" style={{ color: 'var(--t-text-muted)' }} />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <DimIcon className="w-4 h-4" style={{ color: isLocked ? 'var(--t-text-muted)' : (g.color || '#0D9488') }} />
                      <span className="text-[10px] font-semibold truncate" style={{ color: 'var(--t-text-heading)' }}>
                        {g.name}
                      </span>
                    </div>
                    {!isLocked && (
                      <>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-2xl font-black" style={{ color: g.color }}>
                            {isPrelim ? '~' : ''}{g.grade}
                          </span>
                          <span className="text-[10px] font-mono" style={{ color: 'var(--t-text-muted)' }}>
                            {g.score?.toFixed(1)}
                          </span>
                        </div>
                        <p className="text-[9px] mt-1" style={{ color: 'var(--t-text-muted)' }}>
                          {isPrelim ? 'Preliminary' : 'Final'} | {Math.round(g.weight * 100)}% weight
                        </p>
                        <ChevronRight className="absolute top-3 right-3 w-3.5 h-3.5" style={{ color: 'var(--t-text-muted)', opacity: 0.4 }} />
                      </>
                    )}
                    {isLocked && (
                      <p className="text-[9px] mt-1" style={{ color: 'var(--t-text-muted)' }}>
                        {Math.round(g.weight * 100)}% weight
                      </p>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Dimension Detail Drawer (HubSpot-style) */}
      <AnimatePresence>
        {selectedDimension && (() => {
          const dim = selectedDimension;
          const DimIcon = ICON_MAP[dim.icon] || Brain;
          const isPrelim = dim.state === 'preliminary';
          const scorePct = ((dim.score || 0) / 10) * 100;
          const tips = getDimTips(dim.dimension, dim.score);
          const description = DIM_DESCRIPTIONS[dim.dimension] || '';
          const scoreLabel = dim.score >= 9 ? 'Exceptional' : dim.score >= 8 ? 'Strong' : dim.score >= 7 ? 'Good' :
            dim.score >= 6 ? 'Developing' : dim.score >= 5 ? 'Needs Work' : 'Critical';

          return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex justify-end" data-testid="dimension-drawer-overlay"
              onClick={() => setSelectedDimension(null)}>
              <motion.div
                initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="w-full max-w-md h-full overflow-y-auto shadow-2xl"
                style={{ background: 'var(--t-bg-card)', borderLeft: '1px solid var(--t-border)' }}
                onClick={e => e.stopPropagation()}
                data-testid="dimension-drawer">

                {/* Header */}
                <div className="sticky top-0 z-10 px-6 py-5 flex items-center justify-between"
                  style={{ background: 'var(--t-bg-card)', borderBottom: '1px solid var(--t-border)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${dim.color}15` }}>
                      <DimIcon className="w-5 h-5" style={{ color: dim.color }} />
                    </div>
                    <div>
                      <h2 className="text-base font-extrabold" style={{ color: 'var(--t-text-heading)' }}>{dim.name}</h2>
                      <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{isPrelim ? 'Preliminary' : 'Final'} Grade</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedDimension(null)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: 'var(--t-text-muted)' }}
                    data-testid="close-drawer-btn">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-6">
                  {/* Score Hero */}
                  <div className="text-center py-6 rounded-2xl" style={{ background: `${dim.color}08`, border: `1px solid ${dim.color}20` }}>
                    <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-3"
                      style={{ background: `${dim.color}12`, border: `3px solid ${dim.color}` }}>
                      <span className="text-3xl font-black" style={{ color: dim.color }}>
                        {isPrelim ? '~' : ''}{dim.grade}
                      </span>
                    </div>
                    <p className="text-xl font-bold" style={{ color: dim.color }}>
                      {dim.score?.toFixed(1)}/10
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>
                      {scoreLabel}
                    </p>
                  </div>

                  {/* Score Bar */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold" style={{ color: 'var(--t-text-heading)' }}>Score Breakdown</span>
                      <span className="text-xs font-bold" style={{ color: dim.color }}>{Math.round(dim.weight * 100)}% weight</span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
                      <motion.div initial={{ width: 0 }} animate={{ width: `${scorePct}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className="h-full rounded-full" style={{ background: dim.color }} />
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>0</span>
                      <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>5</span>
                      <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>10</span>
                    </div>
                  </div>

                  {/* Percentile Gauge */}
                  <div className="rounded-xl p-4" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                    <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--t-text-muted)' }}>
                      Score Distribution
                    </p>
                    <div className="flex items-end gap-1 h-16">
                      {[1,2,3,4,5,6,7,8,9,10].map(v => {
                        const isActive = Math.round(dim.score || 0) >= v;
                        return (
                          <div key={v} className="flex-1 rounded-t transition-all"
                            style={{
                              height: `${v * 10}%`,
                              background: isActive ? dim.color : 'var(--t-border)',
                              opacity: isActive ? (v <= Math.round(dim.score || 0) ? 1 : 0.3) : 0.2,
                            }} />
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>Low</span>
                      <span className="text-[9px] font-bold" style={{ color: dim.color }}>Your: {dim.score?.toFixed(1)}</span>
                      <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>High</span>
                    </div>
                  </div>

                  {/* Description */}
                  {description && (
                    <div className="rounded-xl p-4" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                      <p className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: 'var(--t-text-muted)' }}>
                        What This Measures
                      </p>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
                        {description}
                      </p>
                    </div>
                  )}

                  {/* Confidence Level */}
                  <div className="rounded-xl p-4" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                    <p className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: 'var(--t-text-muted)' }}>
                      Confidence Level
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {[1,2,3].map(i => (
                          <div key={i} className="w-8 h-2 rounded-full"
                            style={{
                              background: (isPrelim ? i <= 2 : true)
                                ? (isPrelim ? '#F5841F' : '#16A34A')
                                : 'var(--t-border)',
                            }} />
                        ))}
                      </div>
                      <span className="text-xs font-semibold"
                        style={{ color: isPrelim ? '#F5841F' : '#16A34A' }}>
                        {isPrelim ? 'Preliminary' : 'Final'}
                      </span>
                    </div>
                    <p className="text-[11px] mt-2 leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>
                      {isPrelim
                        ? 'This grade is based on discovery responses and initial data. Complete more assessment batteries to increase confidence and accuracy.'
                        : 'This grade has been validated through multiple assessment sources and has high confidence.'}
                    </p>
                  </div>

                  {/* Improvement Tips */}
                  <div>
                    <p className="text-[10px] font-bold tracking-wider uppercase mb-3" style={{ color: 'var(--t-text-muted)' }}>
                      {dim.score >= 8 ? 'How to Maintain & Grow' : 'How to Improve'}
                    </p>
                    <div className="space-y-2">
                      {tips.map((tip, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl"
                          style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-bold"
                            style={{ background: `${dim.color}15`, color: dim.color }}>
                            {i + 1}
                          </div>
                          <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{tip}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="pt-2 pb-4">
                    <Button onClick={() => { setSelectedDimension(null); navigate('/career-batteries'); }}
                      className="w-full h-11 font-semibold rounded-xl text-sm"
                      style={{ background: dim.color, color: '#fff' }}
                      data-testid="drawer-improve-btn">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      {isPrelim ? 'Take Assessment to Improve Accuracy' : 'View Full Report'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Goal Naming Dialog */}
      <AnimatePresence>
        {showGoalDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)' }}
            onClick={() => { setShowGoalDialog(false); setGoalName(''); }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="nx-card p-6 w-full max-w-md" data-testid="goal-dialog"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-extrabold" style={{ color: 'var(--t-text-heading)' }}>Create New Goal</h3>
                <button onClick={() => { setShowGoalDialog(false); setGoalName(''); }}
                  className="p-1 rounded-lg transition-colors" style={{ color: 'var(--t-text-muted)' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs mb-4" style={{ color: 'var(--t-text-muted)' }}>
                Name your career goal to track it separately. You can have up to 3 active goals.
              </p>
              <Input value={goalName} onChange={e => setGoalName(e.target.value)}
                placeholder="e.g., 2026 Career Pivot, QA Lead in Dubai"
                maxLength={50}
                className="mb-4"
                data-testid="goal-name-input"
                onKeyDown={e => e.key === 'Enter' && handleNewGoal()} />
              <div className="flex gap-2">
                <Button onClick={() => { setShowGoalDialog(false); setGoalName(''); }}
                  variant="outline" className="flex-1 h-10 text-xs font-semibold"
                  style={{ borderColor: 'var(--t-border)' }}>
                  Cancel
                </Button>
                <Button onClick={handleNewGoal} disabled={!goalName.trim()}
                  className="flex-1 h-10 text-xs font-bold"
                  style={{ background: goalName.trim() ? '#0D9488' : '#64748b', color: '#fff' }}
                  data-testid="create-goal-btn">
                  <Target className="w-3.5 h-3.5 mr-1.5" /> Create Goal
                </Button>
              </div>
              <p className="text-[10px] text-center mt-3" style={{ color: 'var(--t-text-muted)' }}>
                {sessions.length}/3 goals used
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal (HubSpot-style) */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)' }}
            onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="nx-card p-6 w-full max-w-md" data-testid="delete-goal-dialog"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: '#DC262612' }}>
                  <AlertTriangle className="w-5 h-5" style={{ color: '#DC2626' }} />
                </div>
                <div>
                  <h3 className="text-base font-extrabold" style={{ color: '#DC2626' }}>Delete Goal</h3>
                  <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>This action cannot be undone</p>
                </div>
              </div>
              <div className="rounded-lg p-3 mb-4" style={{ background: '#DC262606', border: '1px solid #DC262615' }}>
                <p className="text-xs font-bold mb-1" style={{ color: 'var(--t-text-heading)' }}>
                  {deleteTarget.goal_name || deleteTarget.pathway_label || 'Unnamed Goal'}
                </p>
                <p className="text-[11px]" style={{ color: 'var(--t-text-muted)' }}>
                  {deleteTarget.answered_count || 0}/{deleteTarget.total_questions || 16} questions answered | {deleteTarget.completion_pct || 0}% complete
                </p>
              </div>
              <p className="text-xs mb-3" style={{ color: 'var(--t-text-secondary)' }}>
                All data associated with this goal will be permanently deleted, including discovery answers,
                intelligence grades, reports, and assessment results linked to this session.
              </p>
              <p className="text-xs font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>
                Type <span style={{ color: '#DC2626' }}>DELETE</span> to confirm:
              </p>
              <Input value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE"
                className="mb-4 text-sm"
                data-testid="delete-confirm-input"
                style={{ borderColor: deleteConfirmText === 'DELETE' ? '#DC2626' : undefined }}
                onKeyDown={e => e.key === 'Enter' && deleteConfirmText === 'DELETE' && handleDeleteSession()} />
              <div className="flex gap-2">
                <Button onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
                  variant="outline" className="flex-1 h-10 text-xs font-semibold"
                  style={{ borderColor: 'var(--t-border)' }}>
                  Cancel
                </Button>
                <Button onClick={handleDeleteSession}
                  disabled={deleteConfirmText !== 'DELETE' || deleting}
                  className="flex-1 h-10 text-xs font-bold"
                  style={{
                    background: deleteConfirmText === 'DELETE' ? '#DC2626' : '#64748b',
                    color: '#fff',
                  }}
                  data-testid="confirm-delete-btn">
                  {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />}
                  Delete Permanently
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
